import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Bot, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import GenerationNotificationModal from '../components/GenerationNotificationModal';

interface Message {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export const MarketingPlanChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApprovalButtons, setShowApprovalButtons] = useState(false);
  const [approvingPlan, setApprovingPlan] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMarketingPlan();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMarketingPlan = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Fetch the marketing plan analysis and agent status
      const { data: configData, error: configError } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_analysis, media_agent_on')
        .eq('id', 0)
        .maybeSingle();

      if (configError) throw configError;

      if (configData && configData.media_agent_analysis) {
        // Create initial messages
        const initialMessages: Message[] = [
          {
            id: '1',
            sender: 'assistant',
            content: 'Perfecto, ahora vamos a armar un plan de marketing para tu marca.',
            timestamp: new Date().toISOString()
          },
          {
            id: '2',
            sender: 'assistant',
            content: configData.media_agent_analysis,
            timestamp: new Date().toISOString()
          }
        ];

        setMessages(initialMessages);
        setShowApprovalButtons(true);
        
        // Save the initial marketing plan message to the chat_plan_marketing table with contains_plan: true
        const welcomeMessage = initialMessages[0];
        const marketingPlanMessage = initialMessages[1];
        
        // Save welcome message
        await supabase
          .schema((profile as any).id_company)
          .from('chat_plan_marketing')
          .insert({
            user_id: user.id,
            sender: welcomeMessage.sender,
            content: welcomeMessage.content,
            timestamp: welcomeMessage.timestamp,
            contains_plan: false
          });
          
        // Save marketing plan message with contains_plan: true
        await supabase
          .schema((profile as any).id_company)
          .from('chat_plan_marketing')
          .insert({
            user_id: user.id,
            sender: marketingPlanMessage.sender,
            content: marketingPlanMessage.content,
            timestamp: marketingPlanMessage.timestamp,
            contains_plan: true
          });
      } else {
        // No marketing plan found, redirect to brand description
        navigate('/redes-sociales/descripcion-brand');
      }
    } catch (err) {
      console.error('Error fetching marketing plan:', err);
      setError(err instanceof Error ? err.message : 'Error loading marketing plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || sending) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setSending(true);
    setShowApprovalButtons(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      // Get user's id_company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      
      // Save user message to chat_plan_marketing table
      await supabase
        .schema((profile as any).id_company)
        .from('chat_plan_marketing')
        .insert({
          user_id: user.id,
          sender: newMessage.sender,
          content: newMessage.content,
          timestamp: newMessage.timestamp,
          contains_plan: false
        });

      // Send message to backend
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN };
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_identidad', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: user.id,
          mode: 'chat',
          message: inputMessage
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Error processing message');
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        content: data.response || 'Lo siento, no pude procesar tu mensaje.',
        timestamp: new Date().toISOString()
      };

      // Save assistant message to chat_plan_marketing table
      await supabase
        .schema((profile as any).id_company)
        .from('chat_plan_marketing')
        .insert({
          user_id: user.id,
          sender: assistantMessage.sender,
          content: assistantMessage.content,
          timestamp: assistantMessage.timestamp,
          contains_plan: false
        });

      setMessages(prev => [...prev, assistantMessage]);
      setShowApprovalButtons(true);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleMarketingPlanApproval = async () => {
    try {
      setApprovingPlan(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Get user's id_company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      
      // Check if media_agent_on is already true
      const { data: configData } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_on')
        .eq('id', 0)
        .maybeSingle();
      
      // Send request to get the parsed marketing plan
      const headers2: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN };
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_identidad', {
        method: 'POST',
        headers: headers2,
        body: JSON.stringify({
          user_id: user.id,
          mode: 'parse'
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Error processing marketing plan');
      }

      // Get the parsed marketing plan
      const parsedMarketingPlan = data.response || 'No se pudo generar un análisis de marketing.';
      
      // Update the config to set media_agent_on to true and save the parsed marketing plan
      const { error: updateError } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .update({ 
          media_agent_on: true,
          media_agent_analysis: parsedMarketingPlan 
        })
        .eq('id', 0);
      
      if (updateError) throw updateError;

      // Only send request to webhook if media_agent_on was not already true
      if (!configData?.media_agent_on) {
        await fetch('https://ai-agency-dt7d.onrender.com/webhook/ecdac889-7227-4a9a-8f5e-1e4786573968', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.id,
            mode: 'start'
          }),
        });
      }

      // Show notification modal before navigating to calendar
      setShowNotificationModal(true);
    } catch (err) {
      console.error('Error handling marketing plan approval:', err);
      setError(err instanceof Error ? err.message : 'Error al aprobar el plan de marketing');
      setApprovingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen max-h-screen bg-[#EFF0F5]">
      {/* Notification Modal */}
      <GenerationNotificationModal 
        isOpen={showNotificationModal} 
        onClose={() => {
          setShowNotificationModal(false);
          navigate('/redes-sociales/calendario');
        }} 
      />
      
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white border-b">
        <h1 className="text-xl font-semibold">Plan de Marketing</h1>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] md:max-w-[75%] p-4 rounded-lg ${
                  message.sender === 'user'
                    ? 'bg-blue-500 text-white ml-4 md:ml-0'
                    : 'bg-white text-gray-800 mr-4 md:mr-0'
                }`}
              >
                {message.sender === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">Asistente</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          
          {sending && (
            <div className="flex justify-start">
              <div className="max-w-[85%] md:max-w-[75%] p-4 rounded-lg bg-white text-gray-800 mr-4 md:mr-0">
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Asistente</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-gray-500">Escribiendo...</span>
                </div>
              </div>
            </div>
          )}
          
          {showApprovalButtons && messages.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-blue-800 font-medium mb-3 sm:mb-0 sm:mr-4 text-center sm:text-left">
                ¿Te parece bien este plan de marketing?
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowApprovalButtons(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                >
                  Quisiera cambiar algo
                </button>
                <button
                  onClick={handleMarketingPlanApproval}
                  disabled={approvingPlan}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                >
                  {approvingPlan ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <span>Me parece bien</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t bg-white p-4">
        <form onSubmit={handleSendMessage} className="max-w-3xl mx-auto">
          <div className="flex gap-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="flex-1 p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !inputMessage.trim()}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarketingPlanChat;